/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Standardized API Timeout Configuration
 *
 * Centralized timeout values for all API operations to ensure consistent
 * behavior and prevent hanging requests.
 *
 * Preview/development environments get extended timeouts to account for
 * serverless cold starts.
 */

// Detect if running in Preview or development
const isPreviewOrDev =
  typeof window !== "undefined" && window.location?.hostname
    ? window.location.hostname.includes("vercel.app") ||
      window.location.hostname === "localhost"
    : process.env.VERCEL_ENV === "preview" ||
      process.env.NODE_ENV === "development";

// Multiplier for Preview/dev environments (2x normal timeouts to account for cold starts)
// Cold starts can add 3-7s for route compilation, plus slower database connections
const PREVIEW_MULTIPLIER = isPreviewOrDev ? 2 : 1;

export const API_TIMEOUTS = {
  // Database operations
  DATABASE: {
    SELECT: 3000, // 3 seconds (reduced from 5s for faster failure detection)
    INSERT: 3000, // 3 seconds
    UPDATE: 3000, // 3 seconds
    DELETE: 2000, // 2 seconds
    DEFAULT: 3000, // 3 seconds (reduced from 5s)
  },

  // External services
  EXTERNAL: {
    OPENAI: 60000, // 60 seconds (generation takes time)
    STRIPE: 10000, // 10 seconds
    EDGE_FUNCTION: 15000, // 15 seconds
    DEFAULT: 10000, // 10 seconds
  },

  // Internal API routes - these get the Preview multiplier applied
  // List operations need more time: cold start (3-7s) + parallel queries (5-15s) + RPC calls (2-5s)
  INTERNAL: {
    CRUD: Math.round(5000 * PREVIEW_MULTIPLIER), // 5s (10s in Preview)
    LIST: Math.round(20000 * PREVIEW_MULTIPLIER), // 20s (40s in Preview) - accounts for cold start + multiple queries
    SEARCH: Math.round(8000 * PREVIEW_MULTIPLIER), // 8s (16s in Preview) - search can be slow with ILIKE
    ANALYTICS: Math.round(20000 * PREVIEW_MULTIPLIER), // 20s (40s in Preview) - RPC calls can be slow
    EXPORT: 30000, // 30 seconds
    PREFERENCES: 30000, // 30 seconds (accounts for route compilation: 7-18s compile + 3-5s render)
    DEFAULT: Math.round(20000 * PREVIEW_MULTIPLIER), // 20s (40s in Preview) - increased for complex endpoints
  },
} as const;

export type TimeoutCategory = "database" | "external" | "internal";
export type DatabaseOperation = keyof typeof API_TIMEOUTS.DATABASE;
export type ExternalOperation = keyof typeof API_TIMEOUTS.EXTERNAL;
export type InternalOperation = keyof typeof API_TIMEOUTS.INTERNAL;
export type TimeoutOperation =
  | DatabaseOperation
  | ExternalOperation
  | InternalOperation;

/**
 * Get timeout value for a specific category and operation
 */
export function getTimeout(
  category: TimeoutCategory,
  operation: TimeoutOperation | "DEFAULT" = "DEFAULT",
): number {
  const categoryUpper = category.toUpperCase() as keyof typeof API_TIMEOUTS;
  const config = API_TIMEOUTS[categoryUpper];

  if (!config) {
    return API_TIMEOUTS.INTERNAL.DEFAULT;
  }

  const operationValue = config[operation as keyof typeof config];
  return operationValue ?? config.DEFAULT;
}

/**
 * Create an AbortController with timeout
 */
export function createTimeoutController(timeoutMs: number): AbortController {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Store timeout ID for cleanup
  (controller as { _timeoutId?: NodeJS.Timeout })._timeoutId = timeoutId;

  return controller;
}

/**
 * Clear timeout for an AbortController
 */
export function clearTimeoutController(controller: AbortController): void {
  const timeoutId = (controller as { _timeoutId?: NodeJS.Timeout })._timeoutId;
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
}
